import { DIRECTIONS } from "../consts/directions.js";
import { PIECE_VALUES } from "../consts/pieces.js";
import match from "../utils/match.js";
import remember from "../utils/remember.js";
import GamePiece from "./game-piece.js";
import GamePieceAssassin from './game-piece-assassin.js';
import GamePieceEmpty from './game-piece-empty.js';
import GamePieceKing from './game-piece-king.js';
import GamePiecePeasant from './game-piece-peasant.js';
import GamePieceUnturned from './game-piece-unturned.js';

const stringifyLocation = (y, x) => `${y}-${x}`;

export default class GameBoard {

    constructor({ board = [], blindKingsGame }) {
        this.rawBoard = board;
        this.blindKingsGame = blindKingsGame;
    }

    get gamePieceByLocation() {
        return remember(this, '__gamePieceByLocation', () => {
            return this.rawBoard.reduce((rowByLocation, row, y) => ({
                ...rowByLocation,
                ...row.reduce((pieceByLocation, piece, x) => {
                    const props = {
                        piece,
                        location: { x, y },
                        followingTooLongPieceId: this.blindKingsGame?.followingTooLongMap?.[piece.id],
                        gameBoard: this,
                    }

                    return {
                        ...pieceByLocation,
                        [stringifyLocation(y, x)]: match(piece.value)
                            .equals(PIECE_VALUES.Empty, () => new GamePieceEmpty(props))
                            .equals(PIECE_VALUES.Peasant, () => new GamePiecePeasant(props))
                            .equals(PIECE_VALUES.Assassin, () => new GamePieceAssassin(props))
                            .equals(PIECE_VALUES.King, () => new GamePieceKing(props))
                            .equals(PIECE_VALUES.Unturned, () => new GamePieceUnturned(props))
                            .otherwise(() => new GamePiece(props)),
                    }
                }, {}),
            }), {});
        })
    }

    get board() {
        return remember(this, '__board', () => (
            this.rawBoard.map((row, y) => (
                row.map((_, x) => this.gamePieceByLocation[stringifyLocation(y, x)])
            ))
        ));
    }

    get allPieces() {
        return remember(this, `__allPieces`, () => this.board.reduce((all, row) => [
            ...all,
            ...row,
        ], []));
    }

    get gamePiecesById() {
        return remember(this, `__gamePiecesById`, () => this.allPieces.reduce((piecesById, piece) => ({
            ...piecesById,
            [piece.id]: piece,
        }), {}))
    }

    get allActiveBoardPieces() {
        return remember(this, `__allActiveBoardPieces`, () => this.allPieces.filter(({ isActive }) => isActive));
    }

    getActivePiecesByTeamIndex = teamIndex => remember(this, `__activePiecesByTeamIndex<${teamIndex}>`, () => (
        this.allActiveBoardPieces.filter(p => p.teamIndex === teamIndex)
    ))

    getPieceByLocation = (y, x) => this.board?.[y]?.[x];

    getAdjacentPieceByLocationAndDirection = (y, x, direction) => match(direction)
        .equals(DIRECTIONS.CENTER, this.getPieceByLocation(y, x))
        .equals(DIRECTIONS.UP, () => this.getPieceByLocation(y + 1, x))
        .equals(DIRECTIONS.DOWN, () => this.getPieceByLocation(y - 1, x))
        .equals(DIRECTIONS.RIGHT, () => this.getPieceByLocation(y, x + 1))
        .equals(DIRECTIONS.LEFT, () => this.getPieceByLocation(y, x - 1))
        .otherwise(undefined);

    getAllAdjacentPiecesByLocation = (y, x) => [DIRECTIONS.UP, DIRECTIONS.DOWN, DIRECTIONS.LEFT, DIRECTIONS.RIGHT]
        .map(direction => this.getAdjacentPieceByLocationAndDirection(y, x, direction))
        .filter(Boolean);

    getAllActiveAdjacentPiecesByLocation = (y, x) => this.getAllAdjacentPiecesByLocation(y, x).filter(({ isActive }) => isActive)

    getDirectionBetweenLocations = (fromLocation, toLocation) => {
        if (fromLocation.x === toLocation.x && fromLocation.y === toLocation.y) return DIRECTIONS.CENTER;
        if (fromLocation.x === toLocation.x && fromLocation.y < toLocation.y) return DIRECTIONS.UP;
        if (fromLocation.x === toLocation.x && fromLocation.y > toLocation.y) return DIRECTIONS.DOWN;
        if (fromLocation.x < toLocation.x && fromLocation.y === toLocation.y) return DIRECTIONS.RIGHT;
        if (fromLocation.x > toLocation.x && fromLocation.y === toLocation.y) return DIRECTIONS.LEFT;
        return;
    }

    availableMovesByTeamIndex = teamIndex => remember(this, `__availableMovesByTeamIndex<${teamIndex}>`, () => (
        this.allPieces
            .filter(piece => piece.teamIndex === teamIndex || piece.isUnturned)
            .reduce((all, { allAvailablePiecesForAction = [] }) => [
                ...all,
                ...allAvailablePiecesForAction,
            ], [])
    ));

    get encodedBoard() {
        return this.allPieces.map(piece => piece.encodedPiece).join('?');
    }

    static decodeBoard(encodedBoard) {
        const allPieces = encodedBoard.split('?').map(GamePiece.decodePiece);
        return Array(4).fill().map((_, y) => Array(8).fill().map((_, x) => allPieces[(y * 8) + x]));
    }
}